import { z } from "zod";
import type { AppConfig } from "@/models/appConfig";
import { AppError } from "@/errors";
import { OptionalKeys } from "@/types";
import poweredByLogo from "@/assets/powered-by-gideo.png";
import devConfig from "@/devConfig";
import type { FetchContext } from "./types";

// ==========================================
// 1. ASSET GLOB HOOKS & BASES
// ==========================================

const imageAssets = import.meta.glob(
    "@sm-account/images/*.{png,jpg,jpeg,gif,svg,webp}",
    {
        eager: true,
        query: "?url",
        import: "default"
    }
);

function getOptionalImage(
    fileName: string,
    useDefault: boolean = false
): string | undefined {
    const imageUrl = useDefault
        ? `/src/assets/${import.meta.env.ACCOUNT_ID || "default"}/images/${fileName}`
        : `/src/assets/${import.meta.env.ACCOUNT_ID}/images/${fileName}`;
    return typeof imageAssets[imageUrl] === "string"
        ? imageAssets[imageUrl]
        : undefined;
}

const domain: string =
    devConfig?.domain ?? import.meta.env.APP_DOMAIN ?? self.location.hostname;
const logoUrl = getOptionalImage("logo.png");
const backgroundUrl = getOptionalImage("background.png");
const landingBackgroundUrl = getOptionalImage("landing.png");

async function imageExists(url?: string): Promise<boolean> {
    if (!url) {
        return false;
    }
    try {
        const response = await fetch(url, { method: "HEAD" });
        return response.ok;
    } catch {
        return false;
    }
}

export const defaultAppConfig: () => AppConfig = () => ({
    accountId: "",
    title: "",
    company: undefined,
    domain,
    assetsURL: "/webtv-assets/",
    apiBaseUrls: {
        ott: import.meta.env.API_BASE_URL,
        auth: import.meta.env.API_BASE_URL,
        store: import.meta.env.API_BASE_URL,
        analytics: undefined
    },
    features: {
        ads: {
            enabled: false,
            settings: { vastUrl: "" }
        },
        authentication: {
            enabled: false,
            settings: { customFields: [] }
        },
        landingPage: {
            enabled: true
        },
        affiliateMarketing: {
            enabled: false,
            settings: { platform: "", publicKey: "" }
        },
        cta: {
            enabled: false,
            settings: {
                title: "",
                logo: undefined,
                description: undefined,
                backgroundImage: undefined,
                videoId: undefined,
                links: []
            }
        },
        recaptcha: {
            enabled: false
        },
        disqus: {
            enabled: false,
            settings: { shortname: "" }
        },
        donations: {
            enabled: false,
            settings: { ctaButtonText: "", ctaMessage: "", terms: "" }
        },
        gifts: { enabled: false },
        ppv: { enabled: false },
        subscriptions: { enabled: false },
        privacyPolicy: { enabled: false },
        termsOfUse: { enabled: false }
    },
    paymentProcessor: undefined,
    videoPlayer: {
        playbackRates: undefined,
        limitPlaybackSessions: false
    },
    ui: {
        showVideoViews: false,
        showVideoDate: false,
        backgroundImage: backgroundUrl,
        landingBackgroundImage: landingBackgroundUrl,
        fallbackPosterImage: logoUrl || "",
        customCSS: undefined,
        footerLogo: [
            {
                href: "https://gideo.video",
                image: poweredByLogo,
                target: "_blank",
                alt: "Powered by Gideo",
                class: "powered-by-logo"
            }
        ],
        logo: logoUrl || "",
        text: {
            signupButton: "Sign up To Watch"
        }
    },
    device: {
        id: "",
        type: "unknown",
        platform: "",
        model: "",
        operatingSystem: "",
        osVersion: "",
        manufacturer: ""
    },
    info: {
        appStore: "web",
        storeUrl: "",
        bundleId: "",
        name: "",
        version: "1.0",
        build: "1"
    }
});

function applyCatchWithWarning<T extends z.ZodType>(
    schema: T,
    defaultValue: z.output<T>
) {
    return schema.catch(ctx => {
        const path = ctx.issues[0]?.path;
        console.warn(
            `[Zod Warning] AppConfig validation failed at: ${path?.join(".") || "unknown path"}, using fallback value: ${JSON.stringify(defaultValue)}`
        );
        for (const issue of ctx.issues) {
            console.warn(issue.message);
        }
        return defaultValue;
    });
}
// Helper function to apply catch with warning for optional values
function catchWarn<T extends z.ZodType>(defaultValue: z.output<T>) {
    return (schema: T) => applyCatchWithWarning(schema, defaultValue);
}

//
function defaultCatchWarn<T extends z.ZodType>(
    defaultValue: Exclude<z.output<T>, undefined>
) {
    return (schema: T) =>
        applyCatchWithWarning(schema.default(defaultValue), defaultValue);
}

// ==========================================
// 2. HYBRID ZOD RUNTIME SHAPE ENGINE
// ==========================================

const RawConfigSchema = z.looseObject({
    // Primitive text catch boundaries replicating typeOrDefault safely
    assetsURL: z.string().apply(defaultCatchWarn("/webtv-assets/")),
    webtv: z.string().apply(defaultCatchWarn(import.meta.env.APP_DOMAIN ?? "")),
    accountId: z
        .string()
        .apply(defaultCatchWarn(import.meta.env.ACCOUNT_ID ?? "")),
    title: z.string().apply(defaultCatchWarn("")),
    company: z.string().optional().apply(catchWarn(undefined)),
    hasSignin: z.boolean().apply(catchWarn(false)),
    affiliateMarketingPlatform: z
        .string()
        .optional()
        .apply(catchWarn(undefined)),
    affiliateMarketingPublicKey: z
        .string()
        .optional()
        .apply(catchWarn(undefined)),
    disqusShortname: z.string().optional().apply(catchWarn(undefined)),
    allowDonations: z.boolean().apply(defaultCatchWarn(false)),
    donateButtonText: z.string().optional().apply(defaultCatchWarn("Donate")),
    donateMessage: z.string().optional().apply(defaultCatchWarn("")),
    donateTerms: z.string().optional().apply(defaultCatchWarn("")),

    // Nested structures with absolute fallback catch structures
    apiBaseUrls: z
        .object({
            ott: z
                .string()
                .apply(catchWarn(import.meta.env.API_BASE_URL ?? "")),
            auth: z
                .string()
                .apply(catchWarn(import.meta.env.API_BASE_URL ?? "")),
            store: z
                .string()
                .apply(catchWarn(import.meta.env.API_BASE_URL ?? "")),
            analytics: z.string().optional().apply(catchWarn(undefined))
        })
        .apply(
            catchWarn({
                ott: import.meta.env.API_BASE_URL,
                auth: import.meta.env.API_BASE_URL,
                store: import.meta.env.API_BASE_URL,
                analytics: undefined
            })
        ),

    // Defer highly variant, arrays, and structural data shapes to direct transformer lookups
    appConfig: z.unknown().optional(),
    bundleId: z
        .record(z.string(), z.string().optional())
        .optional()
        .apply(defaultCatchWarn({})),
    appStoreUrls: z
        .record(z.string(), z.string().optional())
        .optional()
        .apply(defaultCatchWarn({})),
    whiteLabelLogo: z.unknown().optional(),
    vastUrl: z.unknown().optional(),
    oauth: z.unknown().optional(),
    signupFields: z.unknown().optional(),
    cta: z
        .object({
            title: z.string().apply(defaultCatchWarn("")),
            logo: z.string().optional().apply(catchWarn(undefined)),
            description: z.string().optional().apply(catchWarn(undefined)),
            backgroundImage: z.string().optional().apply(catchWarn(undefined)),
            videoId: z.string().optional().apply(catchWarn(undefined)),
            links: z
                .array(
                    z
                        .object({
                            url: z.string().apply(defaultCatchWarn("")),
                            text: z
                                .string()
                                .optional()
                                .apply(catchWarn(undefined)),
                            isExternal: z
                                .boolean()
                                .apply(defaultCatchWarn(false))
                        })
                        .transform(link => {
                            return { ...link, text: link.text ?? link.url };
                        })
                )
                .optional()
                .apply(defaultCatchWarn([]))
        })
        .optional()
        .apply(catchWarn(undefined)),
    links: z.unknown().optional(),
    paymentProcessor: z
        .object({
            brand: z.enum(["stripe", "ezic", "authdotnet"]),
            key: z.string().apply(defaultCatchWarn(""))
        })
        .optional()
        .apply(catchWarn(undefined)),
    device: z.unknown().optional(),
    hasGift: z.boolean().apply(defaultCatchWarn(false)),
    hasPPV: z.boolean().apply(defaultCatchWarn(false)),
    hasSubscriptions: z.boolean().apply(defaultCatchWarn(false)),
    hasPrivacyPolicy: z.boolean().apply(defaultCatchWarn(false)),
    hasTermsOfUse: z.boolean().apply(defaultCatchWarn(false)),
    hasPlaybackSessionLimit: z.boolean().apply(defaultCatchWarn(false)),
    enableLandingPage: z.boolean().optional().apply(catchWarn(undefined)),
    changePlaybackRate: z.boolean().apply(defaultCatchWarn(false)),
    showVideoViews: z.boolean().apply(defaultCatchWarn(false)),
    showVideoDate: z.boolean().apply(defaultCatchWarn(false)),
    signupButtonText: z.string().optional()
});

// ==========================================
// 3. MASTER INTEGRATED TIMELINE CONTEXT
// ==========================================

export const createMasterConfigSchema = (
    appStorePlatform: string,
    devOverrides: Record<string, any> = {}
) => {
    return (
        z
            .unknown()
            // --- STAGE 1: Dynamic Merges ---
            .transform((rawPayload): Record<string, any> => {
                let merged: Record<string, any> =
                    typeof rawPayload === "object" && rawPayload !== null
                        ? { ...rawPayload }
                        : {};

                if (
                    merged.appConfig?.webtv &&
                    typeof merged.appConfig.webtv === "object"
                ) {
                    merged = { ...merged, ...merged.appConfig.webtv };
                }
                if (
                    appStorePlatform !== "web" &&
                    merged.appConfig?.[appStorePlatform] &&
                    typeof merged.appConfig[appStorePlatform] === "object"
                ) {
                    merged = {
                        ...merged,
                        ...merged.appConfig[appStorePlatform]
                    };
                }
                if (import.meta.env.QUASAR_DEV && typeof merged === "object") {
                    merged = { ...merged, ...devOverrides };
                }
                return merged;
            })

            // --- STAGE 2: Explicit Data Typing & Structural Pipeline Boundary ---
            .pipe(RawConfigSchema)

            // --- STAGE 3: Object Mapping ---
            .transform(async (data): Promise<AppConfig> => {
                const res = defaultAppConfig();

                res.info.bundleId = data.bundleId?.[appStorePlatform] ?? "";
                res.info.storeUrl = data.appStoreUrls?.[appStorePlatform] ?? "";

                // Safe evaluation paths replacing typeOrDefault & lodash _get mapping logic
                res.assetsURL = data.assetsURL;
                res.domain = data.webtv;
                const appDomain = res.domain;

                if (appDomain && !/^(https?:\/\/)/i.test(res.assetsURL)) {
                    res.assetsURL = `https://${appDomain}${res.assetsURL}`;
                }

                function prefixUrl(url: string, prefix: string) {
                    return /^(https?:\/\/|data:image\/)/i.test(url)
                        ? url
                        : `${prefix}${url}`;
                }

                // Structural lookups via standard key mappings
                function adjustUrl(key: string, def: string) {
                    const val = (data as Record<string, any>)[key];
                    if (typeof val === "boolean") {
                        return val ? prefixUrl(def, res.assetsURL) : undefined;
                    } else if (typeof val === "string") {
                        return prefixUrl(val, res.assetsURL);
                    }
                    return undefined;
                }

                // White Label Logo Normalization Array Engine
                if (
                    data.whiteLabelLogo &&
                    typeof data.whiteLabelLogo === "object"
                ) {
                    const wl = Array.isArray(data.whiteLabelLogo)
                        ? data.whiteLabelLogo
                        : [data.whiteLabelLogo];
                    let hasCustomWL = false;

                    for (const wll of wl) {
                        if (
                            wll &&
                            typeof wll === "object" &&
                            "image" in wll &&
                            typeof wll.image === "string"
                        ) {
                            const tmpLogo: any = { image: "", alt: "" };
                            const wllImagePrefix =
                                appDomain && /^\/common\//i.test(wll.image)
                                    ? `https://${appDomain}`
                                    : res.assetsURL;

                            tmpLogo.image = prefixUrl(
                                wll.image,
                                wllImagePrefix
                            );

                            if ("alt" in wll && typeof wll.alt === "string") {
                                tmpLogo.alt = wll.alt;
                            }
                            if ("url" in wll && typeof wll.url === "string") {
                                if (/^https?:\/\//i.test(wll.url)) {
                                    tmpLogo.href = wll.url;
                                    tmpLogo.target = "_blank";
                                } else {
                                    const cleanUrl = wll.url.replace(/^\//, "");
                                    tmpLogo.href = `https://${appDomain}/${cleanUrl}`;
                                    tmpLogo.target = "_self";
                                }
                            } else {
                                tmpLogo.href = "#";
                            }
                            if (!hasCustomWL) {
                                hasCustomWL = true;
                                res.ui.footerLogo = [];
                            }
                            res.ui.footerLogo.push(tmpLogo);
                        }
                    }
                }
                // Metadata string routing setup
                res.accountId = data.accountId;
                res.title = data.title;
                res.company = data.company || data.title;
                // API Network endpoint bindings
                res.apiBaseUrls.ott =
                    data.apiBaseUrls.ott || res.apiBaseUrls.ott;
                res.apiBaseUrls.auth =
                    data.apiBaseUrls.auth || res.apiBaseUrls.auth;
                res.apiBaseUrls.store =
                    data.apiBaseUrls.store || res.apiBaseUrls.store;
                res.apiBaseUrls.analytics =
                    data.apiBaseUrls.analytics || res.apiBaseUrls.analytics;
                if (
                    import.meta.env.QUASAR_DEV &&
                    (import.meta.env.DEV_API_URL ||
                        import.meta.env.DEV_API_BASE_AUTH_URL ||
                        import.meta.env.DEV_API_BASE_STORE_URL)
                ) {
                    res.apiBaseUrls = {
                        ...res.apiBaseUrls,
                        ott:
                            import.meta.env.API_BASE_URL ?? res.apiBaseUrls.ott,
                        auth:
                            import.meta.env.DEV_API_BASE_AUTH_URL ??
                            import.meta.env.API_BASE_URL ??
                            res.apiBaseUrls.auth,
                        store:
                            import.meta.env.DEV_API_BASE_STORE_URL ??
                            import.meta.env.API_BASE_URL ??
                            res.apiBaseUrls.store
                    };
                }
                // Video advertisement routing
                let vastUrl: unknown = undefined;
                if (
                    data.vastUrl &&
                    typeof data.vastUrl === "object" &&
                    "webtv" in data.vastUrl
                ) {
                    vastUrl = (data.vastUrl as any).webtv;
                } else {
                    vastUrl = data.vastUrl;
                }
                if (typeof vastUrl === "string") {
                    res.features.ads = { enabled: true, settings: { vastUrl } };
                }
                // Profile access permissions and oauth fields validation loop
                if (data.hasSignin) {
                    res.features.authentication.enabled = true;
                    if (data.oauth && typeof data.oauth === "object") {
                        const oauth: any = data.oauth;
                        if (
                            typeof oauth.discovery === "string" &&
                            typeof oauth.clientId === "string"
                        ) {
                            res.features.authentication.settings.oauth = {
                                discoveryUrl: oauth.discovery,
                                clientId: oauth.clientId,
                                scopes: Array.isArray(oauth.scopes)
                                    ? oauth.scopes
                                    : [],
                                audience:
                                    typeof oauth.audience === "string"
                                        ? oauth.audience
                                        : ""
                            };
                        }
                    }
                    if (data.signupFields && Array.isArray(data.signupFields)) {
                        for (const f of data.signupFields) {
                            if (
                                f &&
                                typeof f === "object" &&
                                typeof f.element === "string" &&
                                !!f.element &&
                                typeof f.id === "number"
                            ) {
                                res.features.authentication.settings.customFields.push(
                                    {
                                        id: f.id,
                                        element: f.element,
                                        type: f.type || "",
                                        name: (f.name || f.label) ?? "",
                                        attributes:
                                            f.attributes &&
                                            typeof f.attributes === "object"
                                                ? f.attributes
                                                : {},
                                        label: f.label ?? "",
                                        options: Array.isArray(f.options)
                                            ? f.options.map((o: any) => ({
                                                  text:
                                                      typeof o.text === "string"
                                                          ? o.text
                                                          : "",
                                                  value:
                                                      typeof o.value ===
                                                      "string"
                                                          ? o.value
                                                          : ""
                                              }))
                                            : [],
                                        required: f.required || false
                                    }
                                );
                            }
                        }
                    }
                }
                // Affiliate platforms
                if (
                    data.affiliateMarketingPlatform &&
                    data.affiliateMarketingPublicKey
                ) {
                    res.features.affiliateMarketing.enabled = true;
                    res.features.affiliateMarketing.settings.platform =
                        data.affiliateMarketingPlatform;
                    res.features.affiliateMarketing.settings.publicKey =
                        data.affiliateMarketingPublicKey;
                }
                // Dynamic Call-To-Action configurations
                if (data.cta) {
                    res.features.cta.enabled = true;
                    res.features.cta.settings = data.cta;
                }
                // Disqus config setup
                if (data.disqusShortname) {
                    res.features.disqus.enabled = true;
                    res.features.disqus.settings.shortname =
                        data.disqusShortname;
                }

                // Fundraising platform fields mapping
                if (data.allowDonations) {
                    res.features.donations.enabled = true;
                    res.features.donations.settings.ctaButtonText =
                        data.donateButtonText;
                    res.features.donations.settings.ctaMessage =
                        data.donateMessage;
                    res.features.donations.settings.terms = data.donateTerms;
                }

                // Feature toggle evaluations
                res.features.gifts.enabled = data.hasGift;
                res.features.ppv.enabled = data.hasPPV;
                res.features.subscriptions.enabled = data.hasSubscriptions;
                res.features.privacyPolicy.enabled = data.hasPrivacyPolicy;
                res.features.termsOfUse.enabled = data.hasTermsOfUse;

                // landing page is enabled if either authentication or subscriptions are enabled unless explicitly disabled
                if (typeof data.enableLandingPage === "boolean") {
                    res.features.landingPage.enabled = data.enableLandingPage;
                } else {
                    res.features.landingPage.enabled =
                        res.features.authentication.enabled ||
                        res.features.subscriptions.enabled;
                }

                // Video media streaming limit allocations
                res.videoPlayer.limitPlaybackSessions =
                    data.hasPlaybackSessionLimit;
                res.videoPlayer.playbackRates = data.changePlaybackRate
                    ? [0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4]
                    : undefined;

                // Payment gateway configuration mapping
                res.paymentProcessor = data.paymentProcessor;

                // Hardware screen layout metrics processing
                if (data.device && typeof data.device === "object") {
                    const dev: any = data.device;
                    const allowedDeviceTypes = [
                        "tv",
                        "mobile",
                        "desktop",
                        "unknown"
                    ] as const;
                    if (
                        typeof dev.type === "string" &&
                        allowedDeviceTypes.includes(dev.type as any)
                    ) {
                        res.device.type = dev.type as typeof res.device.type;
                    }
                }
                // Asynchronous structural file check helper
                const assignOptionalImage = async (
                    property: OptionalKeys<AppConfig["ui"]>,
                    fileName: string
                ) => {
                    const val = (data as Record<string, any>)[property];
                    if (typeof val === "boolean" && val === false) {
                        res.ui[property] = undefined;
                    } else if (appDomain || typeof val === "boolean") {
                        const overrideImage = adjustUrl(property, fileName);
                        res.ui[property] = (await imageExists(overrideImage))
                            ? overrideImage
                            : res.ui[property];
                    } else {
                        const overrideImage = adjustUrl(property, fileName);
                        res.ui[property] = overrideImage ?? res.ui[property];
                    }
                };
                // Concurrent processing of layout asset files
                await assignOptionalImage("backgroundImage", "background.png");
                await assignOptionalImage(
                    "landingBackgroundImage",
                    "landing.png"
                );
                // UI theme element updates
                res.ui.logo =
                    adjustUrl("logo", "logo.png") ??
                    logoUrl ??
                    prefixUrl("logo.png", res.assetsURL);
                res.ui.customCSS = adjustUrl("cssURL", "main.css");
                res.ui.fallbackPosterImage =
                    adjustUrl(
                        "fallbackPosterImage",
                        res.ui.fallbackPosterImage
                    ) ??
                    logoUrl ??
                    prefixUrl("logo.png", res.assetsURL);
                res.ui.showVideoViews = !!data.showVideoViews;
                res.ui.showVideoDate = !!data.showVideoDate;
                res.ui.text = {
                    signupButton:
                        data.signupButtonText ||
                        res.features.subscriptions.enabled
                            ? "Subscribe To Watch"
                            : "Signup To Watch"
                };
                return res;
            })
    );
};

export const parseAppConfig = async (
    data: unknown,
    context: FetchContext = {}
): Promise<AppConfig> => {
    const appStorePlatform = context.appInfo?.info?.appStore ?? "web";
    const pipeline = createMasterConfigSchema(
        appStorePlatform,
        typeof devConfig !== "undefined" ? devConfig : {}
    );
    // Parse the object data across the pipeline stages asynchronously
    const result = await pipeline.safeParseAsync(data);
    console.log("Parse result:", result);
    if (!result.success) {
        throw new AppError("unexpected app config structure", {
            code: "parse_error",
            cause: result.error
        });
    }
    return result.data;
};
